// GtCandidateSequences (C1-SEQ3) — the Sequences tab on a candidate's record.
// Every sequence they are in, one card per role: where it got to, each email
// and when it went or goes, and why it stopped. Opens the run to read, edit or
// act. A sequence can be started from here too (it asks which role).
//
// Props: candidate (the record), authFetch, navigate
import { useState, useEffect, useCallback } from 'react';
import GtSequenceStart from './GtSequenceStart';

const TONE = { queued: 'blue', writing: 'blue', ready: 'amber', failed: 'rose', approved: 'teal', sending: 'teal', paused: 'slate',
  replied: 'green', bounced: 'rose', unsubscribed: 'rose', stopped: 'slate', finished: 'slate' };
function when(d) {
  if (!d) return '';
  return new Date(d).toLocaleString('en-GB', { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}
function stepText(s, q) {
  if (s.skipped) return ['Skipped', 'slate'];
  if (s.message_status === 'sent' || s.message_status === 'delivered') return ['Sent ' + when(s.sent_at), 'green'];
  if (s.message_status === 'queued' || s.message_status === 'sending') return ['Going now', 'teal'];
  if (s.message_status === 'bounced') return ['Bounced', 'rose'];
  if (s.message_status === 'failed') return ['Failed', 'rose'];
  if ((q.status === 'approved' || q.status === 'sending') && s.step === q.current_step + 1 && q.next_send_at) return ['Next, from ' + when(q.next_send_at), 'teal'];
  if (['ready', 'failed', 'queued', 'writing'].indexOf(q.status) !== -1) return [s.step === 1 ? 'Goes first, once approved' : 'Day ' + s.day + ', if no reply', 'slate'];
  if (q.status === 'approved' || q.status === 'sending') return ['Day ' + s.day + ', if no reply', 'slate'];
  return ['Not sent', 'slate'];
}

export default function GtCandidateSequences({ candidate, authFetch, navigate }) {
  const [list, setList] = useState(null);
  const [error, setError] = useState(null);
  const [starting, setStarting] = useState(false);
  const load = useCallback(async () => {
    try { const r = await authFetch('/api/candidates/' + candidate.id + '/sequences'); setList(r.sequences || []); setError(null); }
    catch (e) { setError(e.message); setList([]); }
  }, [authFetch, candidate.id]);
  useEffect(() => { load(); }, [load]);

  const name = candidate.first_name || String(candidate.full_name || '').split(/\s+/)[0] || 'them';
  return (
    <div className="gtsq-cand">
      <div className="gtsq-cand-head">
        <span>Personal emails about a role, written for {name}, approved by you, sent in one thread until they reply.</span>{/* C1-SEQ3B: the tab already says Sequences */}
        <button type="button" className="gtc-btn-ghost" onClick={() => setStarting(true)}>Start a sequence</button>
      </div>
      {error && <div className="gtnt-banner gtnt-banner--rose">{error}</div>}
      {list === null ? <div className="gtsf-muted">Loading…</div> : list.length === 0 ? (
        <div className="gtr2-soon gtsq-cand-soon">
          <b>Not in any sequence yet</b>
          <p>Start one with the button above, or tick {name} on a role's pipeline, a bench or the Candidates list.</p>
        </div>
      ) : list.map((q) => (
        <div key={q.id} className="gtsq-cand-card">
          <div className="gtsq-cand-cardhead">
            <div className="gtsq-cand-role">
              <button type="button" className="gtsq-namebtn gtsq-cand-rolebtn" onClick={() => navigate('/roles/' + (q.role_ref || q.role_id))}>{q.role_title}</button>
              <span className={'gtsq-pill gtsq-pill--' + (TONE[q.status] || 'slate')}>{q.label}</span>
            </div>
            <button type="button" className="gtsf-linkbtn" onClick={() => navigate('/sequences/' + q.run_id)}>Open the sequence</button>
          </div>
          <div className="gtsq-cand-sub">
            {q.plan_name || 'Sequence'} · started {new Date(q.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
            {q.approved_at ? ' · approved by ' + (q.approved_by_name || 'GTCO') + ' ' + new Date(q.approved_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : ''}
            {q.has_linkedin ? ' · LinkedIn messages written' : ''}
          </div>
          {q.status === 'replied' && <div className="gtsq-cand-note gtsq-cand-note--green">Replied {when(q.replied_at)}{q.replied_after_step ? ', after email ' + q.replied_after_step : ''}. Nothing more goes.</div>}
          {['stopped', 'bounced', 'unsubscribed'].indexOf(q.status) !== -1 && q.stop_reason && <div className="gtsq-cand-note">Stopped: {q.stop_reason}.</div>}
          {q.steps.length > 0 && (
            <ol className="gtsq-cand-steps">
              {q.steps.map((s) => {
                const t = stepText(s, q);
                return (
                  <li key={s.step} className={s.skipped ? 'gtsq-cand-step--skip' : ''}>
                    <span className="gtsq-cand-stepno">Email {s.step}</span>
                    <span className="gtsq-cand-stepsubj">{s.step === 1 ? s.subject : 'Re: ' + (q.steps[0].subject || '')}</span>
                    <span className={'gtsq-pill gtsq-pill--' + t[1]}>{t[0]}</span>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      ))}
      {list && list.length > 0 && <div className="gtsf-hint">The emails themselves, and any reply, are on the Emails tab.</div>}
      {starting && (
        <GtSequenceStart candidateIds={[candidate.id]} source={{ kind: 'candidate', id: candidate.id, label: candidate.full_name || null }}
          authFetch={authFetch} navigate={navigate} onClose={() => { setStarting(false); load(); }} />
      )}
    </div>
  );
}
